import { BasisPoints } from '@klear/basis-points'
import { SUBSCRIPTION_TIER_LAUNCH, type SubscriptionTierName } from '@klear/constants/subscription-tier'
import type { RatingScale } from '@klear/constants'
import { Timestamp } from '@klear/time'

export type InvoiceOfferFeeRateIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export type InvoiceOfferFeeRateJson = {
  identity: InvoiceOfferFeeRateIdentity
  ratingScale: RatingScale
  rating: string
  subscriptionTierName: SubscriptionTierName
  annualizedRate: number
  createdAt?: number
  updatedAt?: number
  deletedAt?: number
  createdByUserId?: number
  createdByUserEmail?: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string
}

export function invoiceOfferFeeRateToJson(feeRate: InvoiceOfferFeeRate): InvoiceOfferFeeRateJson {
  return {
    identity: feeRate.identity,
    ratingScale: feeRate.ratingScale,
    rating: feeRate.rating,
    subscriptionTierName: feeRate.subscriptionTierName,
    annualizedRate: feeRate.annualizedRate.value,
    createdAt: feeRate.createdAt,
    updatedAt: feeRate.updatedAt,
    deletedAt: feeRate.deletedAt,
    createdByUserId: feeRate.createdByUserId,
    createdByUserEmail: feeRate.createdByUserEmail,
    updatedByUserId: feeRate.updatedByUserId,
    updatedByUserEmail: feeRate.updatedByUserEmail,
    deletedByUserId: feeRate.deletedByUserId,
    deletedByUserEmail: feeRate.deletedByUserEmail
  }
}

export class InvoiceOfferFeeRate {
  static readonly __klearClass = 'InvoiceOfferFeeRate'

  readonly identity: InvoiceOfferFeeRateIdentity
  ratingScale: RatingScale
  rating: string
  subscriptionTierName: SubscriptionTierName
  annualizedRate: BasisPoints
  createdAt?: number
  updatedAt?: number
  deletedAt?: number
  createdByUserId?: number
  createdByUserEmail?: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string

  constructor(props: Partial<InvoiceOfferFeeRateJson> = {}) {
    const now = Timestamp.now().epochMs
    if (props.identity?.kind === 'saved') {
      this.identity = { kind: 'saved', id: props.identity.id }
    } else if (props.identity?.kind === 'new') {
      this.identity = { kind: 'new' }
    } else {
      this.identity = { kind: 'new' }
    }
    this.ratingScale = props.ratingScale ?? 'IG'
    this.rating = props.rating ?? ''
    this.subscriptionTierName = props.subscriptionTierName ?? SUBSCRIPTION_TIER_LAUNCH
    this.annualizedRate = new BasisPoints(props.annualizedRate ?? 0)
    this.createdAt = props.createdAt ?? now
    this.updatedAt = props.updatedAt ?? this.createdAt
    this.deletedAt = props.deletedAt
    this.createdByUserId = props.createdByUserId
    this.createdByUserEmail = props.createdByUserEmail
    this.updatedByUserId = props.updatedByUserId
    this.updatedByUserEmail = props.updatedByUserEmail
    this.deletedByUserId = props.deletedByUserId
    this.deletedByUserEmail = props.deletedByUserEmail
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  get isSaved(): boolean {
    return this.identity.kind === 'saved'
  }

  get displayLabel(): string {
    return `${this.ratingScale} ${this.rating} - ${this.subscriptionTierName} (${this.annualizedRate})`
  }

  get isIG(): boolean {
    return this.ratingScale === 'IG'
  }

  get isAllianz(): boolean {
    return this.ratingScale === 'Allianz'
  }

  toJSON() {
    return invoiceOfferFeeRateToJson(this)
  }
}
